import { getClient } from 'azure-devops-extension-api'
import {
  WorkItemTrackingRestClient,
  WorkItemTrackingServiceIds
} from 'azure-devops-extension-api/WorkItemTracking'
import { isEmpty, memoize } from 'lodash'
import { computed } from 'vue'
import { useStore } from 'vuex'

import { getAzureService } from '@/utils/azure-devops-helpers/azure-service'
import { AZURE_DEVOPS_SDK } from '@/utils/azure-devops-helpers/general'
import { getAzureProjectContext } from '@/utils/azure-devops-helpers/project-context'
import { handleError } from '@/utils/error-handling'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

export const getWorkItemNavService = async () => {
  return await getAzureService(WorkItemTrackingServiceIds.WorkItemFormNavigationService)
}

export const getAvailableWorkItemTypes = async () => {
  if (!AZURE_DEVOPS_SDK) return []

  const project = getAzureProjectContext()

  if (!project || !project.id) return []

  const client = getClient(WorkItemTrackingRestClient)

  const workItemTypes = await client.getWorkItemTypes(project.id)

  return workItemTypes.filter(type => !type.isDisabled)
}

export const useCreateWorkItem = () => {
  const store = useStore()

  const workItemTypes = computed(() => {
    if (!store) return []

    return store.state.azureDevops.workItems
  })

  const openCreateWorkItemDialog = async workItemKind => {
    const navigationService = await getWorkItemNavService()

    if (!navigationService) return null

    const fields = {}

    const currentUser = AZURE_DEVOPS_SDK.getUser()

    if (currentUser && currentUser.name) {
      fields['System.AssignedTo'] = currentUser.name
    }

    const workItem = await navigationService.openNewWorkItem(workItemKind.name, fields)

    return workItem ?? null
  }

  const createWorkItem = async workItemKindId => {
    const workItemKind = workItemTypes.value.find(type => type.id === workItemKindId)
    if (!workItemKind) {
      handleError({
        error: new Error('Work item type does not exist'),
        expanded: true
      })
      return null
    }

    try {
      return await openCreateWorkItemDialog(workItemKind)
    } catch (error) {
      handleError({ error })
      return null
    }
  }

  const firstWorkItemKindId = computed(() => {
    if (isEmpty(workItemTypes.value)) return null

    return workItemTypes.value[0][OKR_ELEMENT_KEYS.ID]
  })

  const isTryingCreateWorkItemById = id =>
    workItemTypes.value.some(type => type[OKR_ELEMENT_KEYS.ID] === id)

  return {
    workItemTypes,
    createWorkItem,
    firstWorkItemKindId,
    isTryingCreateWorkItemById
  }
}

const workItemCacheKeyResolver = (WI, map) => {
  return `${WI.issueType ?? WI.platformElementType}|${new URLSearchParams(map).toString()}`
}

export const resolveWorkItemColor = memoize((element, workItemsColorsMap) => {
  return (
    workItemsColorsMap[element.issueType] ??
    workItemsColorsMap[element.platformElementType] ??
    element[OKR_ELEMENT_KEYS.LEVEL_COLOR] ??
    `#0078d4`
  )
}, workItemCacheKeyResolver)
